import { CartAddEvent, ThemeEvents } from '@theme/events';

/*
 * eac-perso.js — Personalizacion con sobreprecio real via productos-fee ocultos.
 *
 * Dos responsabilidades:
 *  1) PDP: recalcula el precio en vivo y, al anadir al carrito, mete el producto
 *     principal + los fees elegidos (Trail Eco / Excellence / Texto) en una sola
 *     llamada, vinculados con un token `_perso_id`.
 *  2) Carrito (cualquier pagina/drawer): oculta las lineas-fee, las sincroniza
 *     con la cantidad del producto padre y las elimina si se quita el padre.
 *
 * El dinero se cobra de verdad porque cada fee es una linea real; el cliente no
 * puede tocarla porque la ocultamos y la reconciliamos en cada cambio del carrito.
 */

const FEE_PARENT_PROP = '_fee_parent';
const PERSO_ID_PROP = '_perso_id';
const FEE_LABEL_PROP = '_fee_label';

/* ---------- utilidades ---------- */

function formatMoney(cents) {
  const n = (Number(cents) / 100).toFixed(2).replace('.', ',');
  return n + ' €';
}

function genPersoId() {
  return 'p' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
}

function cartItemsSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
}

async function getCart() {
  const r = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}

/* ---------- Disenador: sube tu diseno con preview ---------- */

class Designer {
  constructor(root, data) {
    this.root = root;
    this.data = data || {};
    this.required = root.dataset.required === 'true';
    this.fileInput = root.querySelector('[data-designer-file]');
    this.art = root.querySelector('[data-designer-art]');
    this.area = root.querySelector('[data-designer-area]');
    this.base = root.querySelector('[data-designer-base]');
    this.textEl = root.querySelector('[data-designer-text]');
    this.controls = root.querySelector('[data-designer-controls]');
    this.scaleInput = root.querySelector('[data-designer-scale]');
    this.posInput = root.querySelector('[data-designer-pos]');
    this.pdfNote = root.querySelector('[data-designer-pdf-note]');
    this.uploadLabel = root.querySelector('[data-designer-upload-label]');
    this.pattern = root.querySelector('[data-designer-pattern]');
    this.file = null;
    this.objectUrl = null;
    this.placement = 'chest';
    this.baseSrc = this.base ? this.base.src : '';
    this.pos = { x: 50, y: 50, w: 60 };      // arte: centro x/y y ancho, en % de la zona
    this.textPos = { x: 50, y: 82 };          // texto: centro x/y, en % de la zona
    this.motif = 26;                          // tamano del motivo del patron, en % del ancho
    this.fullStyle = 'tile';                  // 'tile' (motivo repetido) | 'single' (imagen entera)
    this.styleWrap = root.querySelector('[data-fullstyle-wrap]');
    this.maskUrl = null;
    this.lastGoodMask = null;                 // se reutiliza si la deteccion falla (misma silueta en otro color)
    this.maskDirty = true;
    this.bind();
  }

  bind() {
    if (this.fileInput) this.fileInput.addEventListener('change', () => this.onFile());
    if (this.scaleInput) {
      this.scaleInput.addEventListener('input', () => {
        if (this.placement === 'full') {
          // en estampado completo el slider controla la densidad del motivo
          this.motif = Math.round(Number(this.scaleInput.value) * 0.45); // 20-100 -> 9-45%
          this.renderPattern();
          this.serialize();
        } else {
          this.pos.w = Number(this.scaleInput.value);
          this.render();
        }
      });
    }
    // ubicacion del estampado (pecho / toda la camiseta)
    this.root.querySelectorAll('input[data-designer-placement]').forEach((r) => {
      r.addEventListener('change', () => {
        if (r.checked) this.setPlacement(r.dataset.designerPlacement);
      });
    });
    // estilo del estampado completo (motivo repetido / imagen entera)
    this.root.querySelectorAll('input[data-designer-fullstyle]').forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.fullStyle = r.dataset.designerFullstyle;
        this.renderPattern();
        this.serialize();
      });
    });
    this.bindDrag(this.art, this.pos, false);
    this.bindDrag(this.textEl, this.textPos, true);
  }

  // Cambia entre logo en el pecho y patron repetido por toda la prenda.
  setPlacement(mode) {
    this.placement = mode === 'full' ? 'full' : 'chest';
    this.area.classList.toggle('eac-designer__area--full', this.placement === 'full');
    const isImg = this.file && this.file.type !== 'application/pdf';
    if (this.styleWrap) this.styleWrap.hidden = this.placement !== 'full';
    if (this.placement === 'full') {
      if (this.art) this.art.hidden = true;
      if (this.pattern && isImg) this.pattern.hidden = false;
      if (this.scaleInput) this.scaleInput.value = Math.round(this.motif / 0.45);
      this.ensureMask();
      this.renderPattern();
    } else {
      if (this.pattern) this.pattern.hidden = true;
      if (this.art && isImg) this.art.hidden = false;
      if (this.scaleInput) this.scaleInput.value = this.pos.w;
      this.render();
    }
    this.serialize();
  }

  // Pinta el estampado completo: motivo repetido "a tresbolillo" (2 capas, la
  // segunda desplazada medio motivo) o una unica imagen que cubre la prenda.
  renderPattern() {
    if (!this.pattern || !this.objectUrl) return;
    const url = 'url(' + this.objectUrl + ')';
    if (this.fullStyle === 'single') {
      // imagen entera: una sola pieza centrada; el slider ajusta su ancho
      const size = Math.round(Number(this.scaleInput ? this.scaleInput.value : 50) * 2); // 20-100 -> 40-200%
      this.pattern.style.backgroundImage = url;
      this.pattern.style.backgroundRepeat = 'no-repeat';
      this.pattern.style.backgroundPosition = 'center';
      this.pattern.style.backgroundSize = size + '% auto';
      this.singleSize = size;
      return;
    }
    this.pattern.style.backgroundRepeat = 'repeat';
    const stage = this.pattern.parentElement;
    const w = stage ? stage.clientWidth : 0;
    if (w > 0) {
      const tile = Math.max(10, (w * this.motif) / 100);
      this.pattern.style.backgroundImage = url + ', ' + url;
      this.pattern.style.backgroundSize = tile + 'px auto, ' + tile + 'px auto';
      this.pattern.style.backgroundPosition = '0 0, ' + tile / 2 + 'px ' + tile / 2 + 'px';
    } else {
      this.pattern.style.backgroundImage = url;
      this.pattern.style.backgroundSize = this.motif + '%';
    }
  }

  // Genera una mascara con la silueta de la prenda (prenda = pixel saturado u
  // oscuro frente a fondo claro) para que el patron no pise el fondo.
  ensureMask() {
    if (!this.maskDirty) return;
    this.maskDirty = false;
    const self = this;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const w = 300;
        const h = Math.max(1, Math.round((w * img.naturalHeight) / img.naturalWidth));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const src = ctx.getImageData(0, 0, w, h);
        const out = ctx.createImageData(w, h);
        let shirt = 0;
        for (let i = 0; i < src.data.length; i += 4) {
          const r = src.data[i], g = src.data[i + 1], b = src.data[i + 2];
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          const lum = (mx + mn) / 2, sat = mx - mn;
          const isShirt = sat > 60 || lum < 100;
          if (isShirt) shirt++;
          out.data[i] = out.data[i + 1] = out.data[i + 2] = 255;
          out.data[i + 3] = isShirt ? 255 : 0;
        }
        const coverage = shirt / (w * h);
        if (coverage < 0.08 || coverage > 0.92) {
          // deteccion poco fiable (p.ej. prenda blanca sobre fondo blanco):
          // reutilizar la ultima silueta buena (los mockups por color comparten forma)
          self.maskUrl = self.lastGoodMask;
        } else {
          ctx.putImageData(out, 0, 0);
          const cv2 = document.createElement('canvas');
          cv2.width = w; cv2.height = h;
          const c2 = cv2.getContext('2d');
          c2.filter = 'blur(1.5px)';
          c2.drawImage(cv, 0, 0);
          self.maskUrl = cv2.toDataURL();
          self.lastGoodMask = self.maskUrl;
        }
      } catch (e) {
        self.maskUrl = self.lastGoodMask; // p.ej. canvas contaminado por CORS
      }
      self.applyMask();
    };
    img.onerror = function () { self.maskUrl = null; self.applyMask(); };
    img.src = this.base ? (this.base.currentSrc || this.base.src) : '';
  }

  applyMask() {
    if (!this.pattern) return;
    const st = this.pattern.style;
    if (this.maskUrl) {
      st.webkitMaskImage = 'url(' + this.maskUrl + ')';
      st.maskImage = 'url(' + this.maskUrl + ')';
      st.webkitMaskSize = '100% 100%';
      st.maskSize = '100% 100%';
      st.inset = '0';
    } else {
      // sin mascara fiable: limitar el patron al rectangulo de la prenda
      st.webkitMaskImage = '';
      st.maskImage = '';
      const rect = (this.area.dataset.areaFull || '22,8,56,84').split(',');
      if (rect.length === 4) {
        st.left = rect[0] + '%';
        st.top = rect[1] + '%';
        st.width = rect[2] + '%';
        st.height = rect[3] + '%';
        st.inset = 'auto';
      }
    }
  }

  // Texto en vivo sobre la prenda (contenido + color).
  setText(value, colorHex) {
    if (!this.textEl) return;
    const has = value && value.trim() !== '';
    this.textEl.hidden = !has;
    this.textEl.textContent = has ? value.trim() : '';
    if (colorHex) this.textEl.style.color = colorHex;
    this.renderText();
    this.serialize();
  }

  // Cambia el mockup segun el color elegido (convencion: alt contiene "color:<valor>").
  setShirtColor(colorValue) {
    if (!this.base || !colorValue) return;
    const imgs = this.data.images || [];
    const want = 'color:' + colorValue.trim().toLowerCase();
    const hit = imgs.find((i) => (i.alt || '').toLowerCase().indexOf(want) !== -1);
    const src = hit ? hit.src : this.baseSrc;
    if (this.base.src !== src) {
      this.base.src = src;
      const main = document.getElementById('EacPdpMain');
      if (main && hit) main.src = hit.src;
      // la silueta cambia con la foto: recalcular la mascara del patron
      this.maskDirty = true;
      if (this.placement === 'full') this.ensureMask();
    }
  }

  onFile() {
    const f = this.fileInput.files && this.fileInput.files[0];
    this.file = f || null;
    if (!f) {
      this.root.classList.remove('eac-designer--active');
      this.art.hidden = true;
      if (this.pattern) this.pattern.hidden = true;
      if (this.controls) this.controls.hidden = true;
      if (this.pdfNote) this.pdfNote.hidden = true;
      if (this.posInput) this.posInput.value = '';
      return;
    }
    this.root.classList.add('eac-designer--active');
    if (this.uploadLabel) this.uploadLabel.textContent = 'Cambiar diseno (' + f.name.slice(0, 24) + ')';
    if (f.type === 'application/pdf') {
      this.art.hidden = true;
      if (this.pattern) this.pattern.hidden = true;
      if (this.controls) this.controls.hidden = true;
      if (this.pdfNote) this.pdfNote.hidden = false;
      if (this.posInput) this.posInput.value = '';
      return;
    }
    if (this.pdfNote) this.pdfNote.hidden = true;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(f);
    this.art.src = this.objectUrl;
    if (this.controls) this.controls.hidden = false;
    this.setPlacement(this.placement); // repinta arte o patron segun el modo activo
  }

  render() {
    const st = this.art.style;
    st.width = this.pos.w + '%';
    st.height = 'auto';
    st.left = this.pos.x + '%';
    st.top = this.pos.y + '%';
    st.transform = 'translate(-50%, -50%)';
    this.renderText();
    this.serialize();
  }

  renderText() {
    if (!this.textEl || this.textEl.hidden) return;
    this.textEl.style.left = this.textPos.x + '%';
    this.textEl.style.top = this.textPos.y + '%';
  }

  serialize() {
    if (!this.posInput) return;
    let v = '';
    if (this.file) {
      if (this.placement === 'full' && this.fullStyle === 'single') {
        v = 'estampado completo imagen entera, ancho:' + (this.singleSize || 100) + '% de la prenda';
      } else if (this.placement === 'full') {
        v = 'estampado completo a tresbolillo, motivo:' + Math.round(this.motif) + '% del ancho';
      } else {
        v = 'arte x:' + Math.round(this.pos.x) + '% y:' + Math.round(this.pos.y) +
            '% ancho:' + Math.round(this.pos.w) + '% zona:pecho';
      }
    }
    if (this.textEl && !this.textEl.hidden) {
      v += (v ? ' | ' : '') + 'texto x:' + Math.round(this.textPos.x) + '% y:' + Math.round(this.textPos.y) + '%';
    }
    this.posInput.value = v;
  }

  bindDrag(el, pos, isText) {
    if (!el) return;
    let dragging = false;
    el.addEventListener('pointerdown', (e) => {
      dragging = true;
      try { el.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
      e.preventDefault();
    });
    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const r = this.area.getBoundingClientRect();
      if (!r.width || !r.height) return;
      pos.x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      pos.y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      if (isText) { this.renderText(); this.serialize(); } else { this.render(); }
    });
    el.addEventListener('pointerup', () => { dragging = false; });
    el.addEventListener('pointercancel', () => { dragging = false; });
  }
}

/* ---------- PDP ---------- */

class PersoPdp {
  constructor(form) {
    this.form = form;
    this.data = JSON.parse(document.getElementById('eac-perso-data').textContent);
    this.variants = this.data.variants || [];
    this.optionNames = this.data.optionNames || [];
    this.totalEl = form.querySelector('[data-eac-total]');
    this.baseHidden = form.querySelector('input[name="id"]');
    const designerEl = document.getElementById('EacDesigner');
    this.designer = designerEl ? new Designer(designerEl, this.data) : null;
    this.bind();
    this.refresh();
  }

  bind() {
    this.form.addEventListener('change', () => this.refresh());
    this.form.addEventListener('input', () => this.refresh());
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    // mostrar/ocultar el cuerpo de un extra (p.ej. color de las estrellas)
    this.form.querySelectorAll('input[data-extra-toggle]').forEach((chk) => {
      const body = this.form.querySelector('[data-extra-body="' + chk.dataset.extraToggle + '"]');
      if (!body) return;
      const sync = () => { body.hidden = !chk.checked; };
      chk.addEventListener('change', sync);
      sync();
    });
  }

  // Resuelve la variante seleccionada a partir de los radios options[].
  resolveVariant() {
    if (!this.variants.length) return null;
    if (this.form.querySelector('input[name^="options["]') == null) {
      // producto sin variantes -> primera
      return this.variants[0];
    }
    const chosen = {};
    this.form.querySelectorAll('input[name^="options["]:checked').forEach((inp) => {
      const m = inp.name.match(/^options\[(.+)\]$/);
      if (m) chosen[m[1]] = inp.value;
    });
    return (
      this.variants.find((v) =>
        this.optionNames.every((name, i) => chosen[name] == null || chosen[name] === v.options[i])
      ) || this.variants[0]
    );
  }

  // Suma de sobreprecios activos (en centimos) + lista de fees activos.
  activeFees() {
    const fees = [];
    const tej = this.form.querySelector('input[data-fee-variant][name="properties[Tejido]"]:checked');
    if (tej && tej.dataset.feeVariant) {
      fees.push({ variant: Number(tej.dataset.feeVariant), price: Number(tej.dataset.feePrice), label: tej.dataset.feeLabel || tej.value });
    }
    const texto = this.form.querySelector('[data-eac-texto]');
    if (texto && texto.value.trim() !== '' && texto.dataset.feeVariant) {
      fees.push({ variant: Number(texto.dataset.feeVariant), price: Number(texto.dataset.feePrice), label: texto.dataset.feeLabel || 'Texto personalizado' });
    }
    // extras con checkbox (forro, estrellas...)
    this.form.querySelectorAll('input[data-fee-extra]:checked').forEach((chk) => {
      if (!chk.dataset.feeVariant) return;
      fees.push({ variant: Number(chk.dataset.feeVariant), price: Number(chk.dataset.feePrice), label: chk.dataset.feeLabel || chk.name });
    });
    return fees;
  }

  refresh() {
    const v = this.resolveVariant();
    if (v && this.baseHidden) this.baseHidden.value = v.id;
    const base = v ? v.price : this.data.basePrice;
    const extra = this.activeFees().reduce((s, f) => s + f.price, 0);
    if (this.totalEl) this.totalEl.textContent = formatMoney(base + extra);

    // texto en vivo: mostrar swatches de color y overlay sobre la prenda
    const texto = this.form.querySelector('[data-eac-texto]');
    const colorWrap = this.form.querySelector('[data-texto-color]');
    const colorSel = this.form.querySelector('input[name="properties[Color texto]"]:checked');
    const hasText = texto && texto.value.trim() !== '';
    if (colorWrap) colorWrap.hidden = !hasText;
    if (this.designer) {
      this.designer.setText(hasText ? texto.value : '', colorSel ? colorSel.dataset.textoColorHex : null);
      // color de camiseta: si la variante tiene opcion "Color" y hay mockup para ese color
      const ci = this.optionNames.findIndex((n) => n.toLowerCase().indexOf('color') !== -1);
      if (ci !== -1 && v) this.designer.setShirtColor(v.options[ci]);
    }
  }

  onSubmit(e) {
    e.preventDefault();
    const v = this.resolveVariant();
    if (!v) return;

    // Validacion tejido obligatorio (si la seccion lo marca)
    const reqTejido = this.form.querySelector('.eac-perso__tejido[data-required="true"]');
    if (reqTejido && !reqTejido.querySelector('input[name="properties[Tejido]"]:checked')) {
      this.showError('Selecciona un tejido para continuar.');
      reqTejido.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validacion diseno obligatorio (productos con disenador)
    if (this.designer && this.designer.required && !this.designer.file) {
      this.showError('Sube tu diseno para continuar.');
      this.designer.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const qty = Math.max(1, Number(this.form.querySelector('input[name="quantity"]').value || 1));
    const persoId = genPersoId();
    const fees = this.activeFees();

    // Propiedades visibles del producto principal
    const mainProps = { [PERSO_ID_PROP]: persoId };
    const tej = this.form.querySelector('input[name="properties[Tejido]"]:checked');
    if (tej) mainProps['Tejido'] = tej.value;
    const texto = this.form.querySelector('[data-eac-texto]');
    if (texto && texto.value.trim() !== '') mainProps['Texto'] = texto.value.trim();
    // otras props libres del form (Frase, Notas, extras, posicion del diseno, etc.)
    const hasTexto = texto && texto.value.trim() !== '';
    this.form.querySelectorAll('[name^="properties["]').forEach((inp) => {
      if (inp === tej || inp === texto) return;
      if (inp.type === 'file') return; // el archivo va aparte (FormData)
      if (inp.name === 'properties[Tejido]') return;
      if (inp.name === 'properties[Color texto]' && !hasTexto) return; // color solo si hay texto
      if ((inp.type === 'radio' || inp.type === 'checkbox') && !inp.checked) return;
      if (inp.closest('[hidden]')) return; // dentro de un bloque oculto/desactivado
      const m = inp.name.match(/^properties\[(.+)\]$/);
      if (m && inp.value && inp.value.trim() !== '') mainProps[m[1]] = inp.value;
    });

    const items = [{ id: Number(v.id), quantity: qty, properties: mainProps }];
    fees.forEach((f) => {
      items.push({
        id: f.variant,
        quantity: qty,
        properties: {
          [PERSO_ID_PROP]: persoId,
          [FEE_PARENT_PROP]: String(v.id),
          [FEE_LABEL_PROP]: f.label,
        },
      });
    });

    const designFile = this.designer && this.designer.file ? this.designer.file : null;
    this.addToCart(items, v.id, qty, designFile);
  }

  showError(msg) {
    let box = this.form.querySelector('#EacAtcError');
    if (box) {
      box.textContent = msg;
      box.hidden = false;
    }
  }

  async addToCart(items, variantId, qty, designFile) {
    const btn = this.form.querySelector('.pdp-atc');
    if (btn) btn.setAttribute('aria-busy', 'true');
    const sections = cartItemsSectionIds().join(',');
    try {
      let res;
      if (designFile) {
        // Con archivo: el producto principal va en multipart (Shopify sube el
        // archivo a su CDN y guarda la URL en la property "Diseño").
        const main = items[0];
        const fd = new FormData();
        fd.append('id', String(main.id));
        fd.append('quantity', String(main.quantity));
        Object.keys(main.properties || {}).forEach((k) => fd.append('properties[' + k + ']', main.properties[k]));
        fd.append('properties[Diseño]', designFile, designFile.name);
        const feeItems = items.slice(1);
        if (!feeItems.length) fd.append('sections', sections);
        const r1 = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: fd,
        });
        res = await r1.json();
        if (res.status) {
          this.showError(res.description || res.message || 'No se pudo anadir al carrito.');
          return;
        }
        if (feeItems.length) {
          const r2 = await fetch(Theme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: feeItems, sections, sections_url: window.location.pathname }),
          });
          res = await r2.json();
          if (res.status) {
            this.showError(res.description || res.message || 'No se pudo anadir el suplemento.');
            return;
          }
        }
      } else {
        const r = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        res = await r.json();
        if (res.status) {
          this.showError(res.description || res.message || 'No se pudo anadir al carrito.');
          return;
        }
      }
      const cart = await getCart();
      document.dispatchEvent(
        new CartAddEvent(cart, 'eac-perso', {
          source: 'eac-perso',
          itemCount: qty,
          variantId: String(variantId),
          sections: res.sections,
        })
      );
    } catch (e) {
      this.showError('Error de conexion. Intentalo de nuevo.');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }
}

/* ---------- Carrito: ocultar + reconciliar fees ---------- */

const CartPerso = {
  busy: false,

  injectStyle() {
    if (document.getElementById('eac-perso-cart-style')) return;
    const s = document.createElement('style');
    s.id = 'eac-perso-cart-style';
    s.textContent =
      '.cart-items__table-row[data-eac-fee]{display:none !important;}' +
      '.eac-perso-note{font-size:11px;color:var(--brand-grey-text,#636563);margin-top:4px;line-height:1.4;}';
    document.head.appendChild(s);
  },

  isFee(item) {
    return item.properties && item.properties[FEE_PARENT_PROP] != null;
  },

  groupKey(item) {
    return item.properties ? item.properties[PERSO_ID_PROP] : null;
  },

  async reconcile() {
    if (this.busy) return;
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      return;
    }
    const items = cart.items || [];
    // mapa persoId -> { parentQty, fees:[{key,qty}] }
    const groups = {};
    items.forEach((it) => {
      const gid = this.groupKey(it);
      if (!gid) return;
      groups[gid] = groups[gid] || { parentQty: null, fees: [] };
      if (this.isFee(it)) groups[gid].fees.push({ key: it.key, qty: it.quantity });
      else groups[gid].parentQty = it.quantity;
    });

    const changes = [];
    Object.keys(groups).forEach((gid) => {
      const g = groups[gid];
      g.fees.forEach((fee) => {
        const target = g.parentQty == null ? 0 : g.parentQty; // padre fuera -> borrar
        if (fee.qty !== target) changes.push({ id: fee.key, quantity: target });
      });
    });

    if (changes.length) {
      this.busy = true;
      try {
        let res = null;
        for (const ch of changes) {
          res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: ch.id,
              quantity: ch.quantity,
              sections: cartItemsSectionIds().join(','),
              sections_url: window.location.pathname,
            }),
          }).then((r) => r.json());
        }
        if (res) {
          document.dispatchEvent(
            new CartAddEvent(res, 'eac-perso-reconcile', { source: 'eac-perso', sections: res.sections })
          );
        }
      } finally {
        this.busy = false;
      }
      return; // el evento disparara otra pasada que ya ocultara
    }

    this.decorate(items);
  },

  // Oculta filas-fee y anota el sobreprecio bajo el producto padre.
  decorate(items) {
    const feeKeys = [];
    const parentNotes = {}; // persoId -> [labels]
    items.forEach((it) => {
      if (this.isFee(it)) {
        feeKeys.push(it.key);
        const gid = this.groupKey(it);
        const label = it.properties[FEE_LABEL_PROP] || 'Personalizacion';
        (parentNotes[gid] = parentNotes[gid] || []).push(label + ' +' + formatMoney(it.price));
      }
    });
    feeKeys.forEach((key) => {
      const row = document.querySelector(`.cart-items__table-row[data-key="${key}"]`);
      if (row) row.setAttribute('data-eac-fee', '');
    });
    // anotar padres
    items.forEach((it) => {
      if (this.isFee(it)) return;
      const gid = this.groupKey(it);
      if (!gid || !parentNotes[gid]) return;
      const row = document.querySelector(`.cart-items__table-row[data-key="${it.key}"]`);
      if (!row) return;
      const host = row.querySelector('.cart-items__product-info') || row.querySelector('.cart-items__details');
      if (!host) return;
      let note = row.querySelector('.eac-perso-note');
      if (!note) {
        note = document.createElement('div');
        note.className = 'eac-perso-note';
        host.appendChild(note);
      }
      note.textContent = 'Personalizacion: ' + parentNotes[gid].join(' · ');
    });
  },

  init() {
    this.injectStyle();
    const run = () => this.reconcile();
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      // tras el morph del tema
      requestAnimationFrame(() => setTimeout(run, 30));
    });
    if (document.readyState !== 'loading') run();
    else document.addEventListener('DOMContentLoaded', run);
  },
};

/* ---------- upsell del drawer: "Completa tu carrito" ---------- */

function bindUpsell() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-eac-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const r = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(btn.dataset.eacUpsellAdd), quantity: 1 }],
          sections: cartItemsSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const res = await r.json();
      if (res.status) throw new Error(res.message || 'error');
      const cart = await getCart();
      document.dispatchEvent(new CartAddEvent(cart, 'eac-upsell', { source: 'eac-upsell', sections: res.sections }));
    } catch (err) {
      btn.disabled = false;
      btn.textContent = '+';
    }
  });
}

/* ---------- arranque ---------- */

function boot() {
  const form = document.getElementById('EacPdpForm');
  if (form && document.getElementById('eac-perso-data')) {
    try {
      new PersoPdp(form);
    } catch (e) {
      /* si falla, el form nativo sigue funcionando */
    }
  }
  CartPerso.init();
  bindUpsell();
}

if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
